/**
 * Publish workflow request + response schemas (issue #12).
 *
 * The publish endpoints flip a draft `ContentVersion` / `PageVersion` to
 * `PUBLISHED`, archive the previously-published peer (if any), and write a
 * `PublishEvent` row — all atomically inside a `prisma.$transaction` with a
 * `SELECT ... FOR UPDATE` lock on the parent row.
 *
 *   POST /api/admin/content-versions/:id/publish
 *   POST /api/admin/page-versions/:id/publish
 *
 * Spec §7 Publishing Logic, §19 Database-Level Invariants.
 *
 * Slice boundary:
 *   - Issue #12 owns this file.
 *   - Issue #14 (ContentFlag CRUD) ships a separate flag schema module so the
 *     two PRs never touch the same shared file. The flag-gate on publish is
 *     enforced server-side via direct Prisma queries; the request body here
 *     only carries the `overrideFlags` opt-in.
 */

import { z } from "zod";

/**
 * Body schema for `POST /api/admin/content-versions/:id/publish`.
 *
 * `mode` (optional, defaults to `"primary"`): the kind of publish to perform.
 *   - `"primary"` — the default. Flips DRAFT → PUBLISHED and demotes the
 *     prior PUBLISHED peer (if any) to ARCHIVED in the same transaction.
 *     This is the historical behaviour the body has always done.
 *   - `"experiment"` — issue #243. Flips DRAFT → EXPERIMENT with no
 *     demotion of the existing PUBLISHED peer. Used when the operator
 *     wants to expose a variant to experiment traffic only.
 *
 * `overrideFlags` (optional, defaults to false): when the target version has
 * unresolved HIGH/CRITICAL `ContentFlag` rows attached, the publish is
 * refused with 422 `PUBLISH_BLOCKED_BY_FLAGS` unless this flag is `true`. We
 * record the override + the list of bypassed flag ids in
 * `PublishEvent.metadataJson` so the audit trail is complete (spec §7).
 * Applies to both publish modes — an experiment publish that gets in front
 * of real users still requires a flag override.
 *
 * `.strict()` rejects unknown top-level keys so a typo like `overideFlags`
 * surfaces as a 422 instead of being silently dropped.
 *
 * The body is allowed to be entirely empty (`{}`) — the common case is a
 * primary publish with no override.
 */
export const publishContentVersionSchema = z
  .object({
    mode: z.enum(["primary", "experiment"]).optional(),
    overrideFlags: z.boolean().optional(),
  })
  .strict();
export type PublishContentVersionInput = z.infer<typeof publishContentVersionSchema>;

/**
 * Body schema for `POST /api/admin/page-versions/:id/publish`.
 *
 * Page publishes have no flag gate — flags only attach to ContentVersions
 * in the spec — but they share the `mode` discriminator with the content
 * publish endpoint (issue #243). `"primary"` (default) flips DRAFT →
 * PUBLISHED and demotes the prior PUBLISHED peer; `"experiment"` flips
 * DRAFT → EXPERIMENT with no demotion.
 */
export const publishPageVersionSchema = z
  .object({
    mode: z.enum(["primary", "experiment"]).optional(),
  })
  .strict();
export type PublishPageVersionInput = z.infer<typeof publishPageVersionSchema>;

/**
 * Body schema for `POST /api/admin/content-versions/:id/promote` (issue
 * #243). Transitions an EXPERIMENT-status ContentVersion to PUBLISHED,
 * demoting the prior PUBLISHED peer to ARCHIVED — the "ship the experiment
 * winner" workflow. Empty body; the action is fully described by the URL.
 */
export const promoteContentVersionSchema = z.object({}).strict();
export type PromoteContentVersionInput = z.infer<typeof promoteContentVersionSchema>;

/**
 * Body schema for `POST /api/admin/page-versions/:id/promote` (issue #243).
 * Page-side analog of the content promote endpoint.
 */
export const promotePageVersionSchema = z.object({}).strict();
export type PromotePageVersionInput = z.infer<typeof promotePageVersionSchema>;

/**
 * Body schema for `POST /api/admin/content-versions/:id/archive-experiment`
 * (issue #243). Transitions an EXPERIMENT ContentVersion → ARCHIVED — the
 * "we tested it, didn't win" action. Empty body.
 */
export const archiveExperimentContentVersionSchema = z.object({}).strict();
export type ArchiveExperimentContentVersionInput = z.infer<
  typeof archiveExperimentContentVersionSchema
>;

/**
 * Body schema for `POST /api/admin/page-versions/:id/archive-experiment`
 * (issue #243). Page-side analog of the content archive endpoint.
 */
export const archiveExperimentPageVersionSchema = z.object({}).strict();
export type ArchiveExperimentPageVersionInput = z.infer<typeof archiveExperimentPageVersionSchema>;

/**
 * Response shape for both publish endpoints. The publish flow updates two
 * versions inside one transaction:
 *   - the target version flips DRAFT → PUBLISHED,
 *   - the previously-published peer (if any) flips PUBLISHED → ARCHIVED,
 * and a `PublishEvent` row is appended. The response surfaces the published
 * version so the client can render the new live state without a follow-up
 * GET; `archivedVersionId` lets the UI show "v1 archived, v2 now live"
 * without inferring it from the event log.
 */
export type PublishContentVersionResponse = {
  publishedVersionId: string;
  archivedVersionId: string | null;
  publishEventId: string;
  /**
   * The lifecycle status the target landed in (issue #243). `"PUBLISHED"`
   * for `mode: "primary"`, `"EXPERIMENT"` for `mode: "experiment"`. The
   * SPA branches on this to decide which slot the row joins after a
   * publish (Primary vs Experiment).
   */
  status: "PUBLISHED" | "EXPERIMENT";
};

export type PublishPageVersionResponse = {
  publishedVersionId: string;
  archivedVersionId: string | null;
  publishEventId: string;
  /** Same semantics as on the content publish response. */
  status: "PUBLISHED" | "EXPERIMENT";
};

/**
 * Response shape for `POST .../promote`. Returns the newly-PUBLISHED
 * version id and the version that was demoted PUBLISHED → ARCHIVED in
 * the same transaction (null when no prior PUBLISHED peer existed).
 */
export type PromoteContentVersionResponse = {
  publishedVersionId: string;
  archivedVersionId: string | null;
  publishEventId: string;
};

export type PromotePageVersionResponse = {
  publishedVersionId: string;
  archivedVersionId: string | null;
  publishEventId: string;
};

/**
 * Response shape for `POST .../archive-experiment`. The EXPERIMENT
 * version flips to ARCHIVED with no replacement.
 */
export type ArchiveExperimentContentVersionResponse = {
  archivedVersionId: string;
  publishEventId: string;
};

export type ArchiveExperimentPageVersionResponse = {
  archivedVersionId: string;
  publishEventId: string;
};
